import type {
  LoaderFunction,
  MetaDescriptor,
  MetaFunction,
} from "@remix-run/node";

import getPublishedMarkets from "./getPublishedMarkets";

/**
 * Fallback tags for SEO.
 */

const fallbackSiteName = "NordicPosterStore";
const fallbackSiteDescription = `Discover the Best Selling Posters and Wall Arts at NordicPosterStore - Shop Now!
  
  Explore our curated collection of the most beloved posters at NordicPosterStore. From captivating meat cuts to intricate city maps and stunning animal portraits featuring elephants, lions, and deer, our posters are inspired by Scandinavian design, blending seamlessly into any interior decor style.
  
  Did you know that posters originally used for sports and circus advertisements, posters evolved to serve educational purposes, featuring world maps, animal anatomy, abstract, text and botanical prints. Today, posters remain a widely recognized and affordable form of art.
  
  Enhance your posters with our exquisite frames, elevating them into captivating wall art pieces. With a range of sizes available, including 13x18cm, 21x30cm (A4), 30x40cm, 40x50cm, 50x50cm, 50x70cm, and 70x100cm, you can create the perfect gallery wall to suit your space.
  
  Looking for the perfect gift? Our framed posters make excellent presents. Choose from a variety of stylish designs, all printed on high-quality, eco-friendly paper and FSC certified premium matter paper.
  
  Discover the Entire Poster Collection, Wall art online at NordicPosterStore.
  
  Browse our extensive range of posters suitable for all ages and rooms in your home. From vibrant kitchen herb prints to sophisticated black and white designs (B&W), adorn your living room with world maps or create a charming nursery with adorable animal posters. Transform your office with serene nature prints or add a touch of elegance to your beauty salon or walk-in closet with trendy fashion posters.
  
  With endless possibilities, find your favorite posters today at NordicPosterStore and elevate your space with artistry and style. Buy posters online conveniently from our online store and experience uncompromising quality and design.
    `;

/**
 * Get canonical meta lnik.
 */
export const crateCanonicalLink = (pathname: string) => {
  const pathnameWithTrailingSlash = pathname.endsWith("/")
    ? pathname
    : `${pathname}/`;

  const canonicalUrl = `https://www.nordicposterstore.com${pathnameWithTrailingSlash}`;

  return {
    tagName: "link",
    rel: "canonical",
    href: canonicalUrl,
  };
};

interface Parameters {
  title?: string | null;
  description?: string | null;
  ogType?: string | null;
  ogUrl?: string | null;
  image?: string | null;
  locale?: string | null;
  twitterCard?: string | null;
  noIndex?: boolean | null;
}

/**
 * Creates a meta array for Remix.
 * With the common meta tags for SEO to be used on all pages.
 */
export const getPageMeta = ({
  title,
  description,
  ogType = "website",
  ogUrl = "https://nordicposterstore.com/",
  image,
  twitterCard = "summary_large_image",
  locale,
  noIndex,
}: Parameters) => {
  const meta: MetaDescriptor[] = [];

  if (title) {
    meta.push(
      {
        title,
      },
      {
        property: "og:type",
        content: ogType,
      },
      {
        property: "og:locale",
        content: locale,
      },
      {
        property: "og:title",
        content: title,
      },
      {
        property: "og:site_name",
        content: "NordicPosterStore",
      },
      {
        property: "og:url",
        content: ogUrl,
      },
      {
        name: "twitter:title",
        content: title,
      }
    );
  }

  if (description) {
    meta.push(
      {
        name: "description",
        content: description,
      },
      {
        property: "og:description",
        content: description,
      },
      {
        name: "twitter:description",
        content: description,
      }
    );
  }

  if (image) {
    meta.push(
      {
        property: "og:image",
        content: image,
      },
      {
        name: "twitter:image",
        content: image,
      }
    );
  }

  if (twitterCard) {
    meta.push({
      name: "twitter:card",
      content: twitterCard,
    });
  }

  if (noIndex) {
    meta.push({
      name: "robots",
      content: "noindex",
    });
  }

  return meta;
};

/**
 * Returns fallback Remix meta array, to be used when no meta tags are provided.
 * This function is to be used in `root`
 */
export const getFallbackMeta = (locale?: string | null) => {
  const title = "NordicPosterStore";
  const description =
    locale === "se"
      ? "Där nordisk design möter heminredning. Handla vårt kuraterade urval av posters och tryck från Skandinavien och bortom."
      : "Where Nordic designs meet home decor. Shop our curated selection of posters and prints from Scandinavia and beyond.";

  const image =
    locale === "se"
      ? "https://cdn.sanity.io/images/n8c7yri3/production/6f832673ca54877e056b01898b49e788d05c1390-1080x1080.png?w=800&fit=max&auto=format"
      : "https://cdn.sanity.io/images/n8c7yri3/production/11f362cfe972bad5c8c5ce1a5d8d396ee612e516-1080x1080.png?w=800&fit=max&auto=format";

  return getPageMeta({
    title,
    description,
    image: image,
  });
};

/**
 * Merges meta from parent routes with the current route overrides.
 */
export const mergeMeta = <
  Loader extends LoaderFunction | unknown,
  ParentsLoaders extends Record<string, LoaderFunction>,
>(
  overrideFn: MetaFunction<Loader, ParentsLoaders>
): MetaFunction<Loader, ParentsLoaders> => {
  return (metaArguments) => {
    const mergedMeta: MetaDescriptor[] = [];

    // get meta from parent routes
    for (const match of metaArguments.matches) {
      mergedMeta.push(...match.meta);
    }

    // replace any parent meta with the same name or property with the override
    const overrides = overrideFn(metaArguments);

    for (const override of overrides) {
      const index = mergedMeta.findIndex((meta) => {
        return (
          ("name" in meta &&
            "name" in override &&
            meta.name === override.name) ||
          ("property" in meta &&
            "property" in override &&
            meta.property === override.property) ||
          ("title" in meta && "title" in override)
        );
      });

      if (index === -1) {
        mergedMeta.push(override);
      } else {
        mergedMeta.splice(index, 1, override);
      }
    }

    return mergedMeta;
  };
};

export const getAlternateLinks = (pathname: string) => {
  const markets = getPublishedMarkets();

  const splitPathname = pathname.split("/").filter((segment) => {
    return Boolean(segment);
  });

  const locales = markets.map(({ locale }) => {
    return locale;
  });

  const pathLocale = splitPathname[0];

  // Remove any potential locale from the pathname
  if (locales.includes(pathLocale)) {
    splitPathname.splice(0, 1);
  }

  const formattedPathname = splitPathname.join("/");

  const alternateLinks = markets.map(
    ({ locale, languageCode, countryCode }) => {
      const alternateUrl = `https://www.nordicposterstore.com/${locale}/${formattedPathname}/`;

      const hrefLang =
        locale === "global" ? languageCode : `${languageCode}-${countryCode}`;

      return {
        tagName: "link",
        rel: "alternate",
        href: alternateUrl.endsWith("/")
          ? alternateUrl.slice(0, -1)
          : alternateUrl,
        hrefLang: `${languageCode}-${countryCode}`,
      };
    }
  );

  return alternateLinks;
};

export const getWebsiteLDJsonMeta = ({
  name = fallbackSiteName,
  description = fallbackSiteDescription,
}: {
  name?: string | null;
  description?: string | null;
}) => {
  return {
    "script:ld+json": {
      "@context": "http://schema.org/",
      "@type": "WebSite",
      name: name,
      description: description,

      url: "https://nordicposterstore.com/",
      sameAs: [
        "https://instagram.com/NordicPosterStore/",
        "http://www.linkedin.com/company/NordicPosterStore",
      ],
      potentialAction: [
        {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: "https://nordicposterstore.com/search/?query={term}",
          },
          "query-input": {
            "@type": "PropertyValueSpecification",
            valueRequired: true,
            valueName: "term",
          },
        },
      ],
    },
  };
};

export const getWebPageLDJsonMeta = ({
  languageCode = "sv-SE",
  name = fallbackSiteName,
  description = fallbackSiteDescription,
}: {
  languageCode?: string | null;
  name?: string | null;
  description?: string | null;
}) => {
  return {
    "script:ld+json": {
      "@context": "http://schema.org/",
      "@type": "WebPage",
      "@id": "https://nordicposterstore.com/",
      url: "https://nordicposterstore.com/",
      name: name,
      description: description,
      isPartOf: { "@id": "https://nordicposterstore.com/#website" },
      datePublished: "2024-10-10T15:48:22+00:00",
      dateModified: "2024-10-30T18:03:35+00:00",
      breadcrumb: { "@id": "https://nordicposterstore.com/#breadcrumb" },
      inLanguage: languageCode,
      potentialAction: [
        {
          "@type": "SearchAction",
          target: "https://nordicposterstore.com/search/?query={term}",
          query: "required",
          "query-input": "required name=term",
        },
        { "@type": "ReadAction", target: ["https://nordicposterstore.com/"] },
      ],

      sameAs: [
        "https://instagram.com/NordicPosterStore/",
        "http://www.linkedin.com/company/NordicPosterStore",
      ],
    },
  };
};

export const getOrganizationLDJsonMeta = () => {
  return {
    "script:ld+json": {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "NordicPosterStore",
      url: "https://nordicposterstore.com/",
      logo: "https://-logo.svg",
      foundingDate: "2024-09-16",
      sameAs: [
        "https://instagram.com/NordicPosterStore/",
        "http://www.linkedin.com/company/NordicPosterStore",
      ],
      contactPoint: [
        {
          "@type": "ContactPoint",
          email: "hey@nordicposterstore.com",
          contactType: "customer service",
        },
      ],
    },
  };
};
